package com.trevorwiebe.timesheet.admin.carrier.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.carrier.domain.CarrierRepository
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class CarriersViewModel(
    private val carrierRepository: CarrierRepository
) : DisposableViewModel() {

    private val _state = MutableStateFlow(CarriersState())
    val state = _state.asStateFlow()

    init {
        loadCarriers()
    }

    fun onEvent(event: CarriersEvent) {
        when (event) {
            is CarriersEvent.OnAddCarrierClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingCarrier = null) }
            }
            is CarriersEvent.OnEditCarrierClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingCarrier = event.carrier) }
            }
            is CarriersEvent.OnDismissDialog -> {
                _state.update { it.copy(showAddEditDialog = false, editingCarrier = null) }
            }
            is CarriersEvent.OnSaveCarrier -> {
                saveCarrier(event)
            }
            is CarriersEvent.OnDeleteCarrier -> {
                deleteCarrier()
            }
            is CarriersEvent.OnRestoreCarrier -> {
                restoreCarrier()
            }
            is CarriersEvent.OnFilterToggled -> {
                _state.update { state ->
                    val current = state.activeFilters
                    val updated = if (event.filter in current) current - event.filter else current + event.filter
                    state.copy(activeFilters = updated)
                }
            }
            is CarriersEvent.OnReorderCarriers -> {
                reorderCarriers(event.orderedIds)
            }
            is CarriersEvent.OnErrorShown -> {
                _state.update { it.copy(error = null) }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadCarriers() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val flow = carrierRepository.getCarriers()
            flow.collect { result ->
                if (result.error != null) {
                    _state.update { it.copy(isLoading = false, error = result.error) }
                } else {
                    val carriers = result.data as List<Carrier>
                    _state.update { it.copy(isLoading = false, carriers = carriers, error = null) }
                }
            }
        }
    }

    private fun saveCarrier(event: CarriersEvent.OnSaveCarrier) {
        viewModelScope.launch {
            val editing = _state.value.editingCarrier
            val result = if (editing == null) {
                carrierRepository.addCarrier(
                    Carrier(
                        id = "",
                        contactName = event.contactName.trim(),
                        companyName = event.companyName.trim(),
                        address = event.address.trim(),
                        phone = event.phone.trim(),
                        email = event.email.trim(),
                        notes = event.notes.trim(),
                        active = event.active,
                        deleted = false,
                        order = (_state.value.carriers.maxOfOrNull { it.order } ?: -1) + 1
                    )
                )
            } else {
                carrierRepository.updateCarrier(
                    editing.copy(
                        contactName = event.contactName.trim(),
                        companyName = event.companyName.trim(),
                        address = event.address.trim(),
                        phone = event.phone.trim(),
                        email = event.email.trim(),
                        notes = event.notes.trim(),
                        active = event.active
                    )
                )
            }
            _state.update {
                it.copy(showAddEditDialog = false, editingCarrier = null, error = result.error)
            }
        }
    }

    private fun deleteCarrier() {
        viewModelScope.launch {
            val editing = _state.value.editingCarrier ?: return@launch
            val result = carrierRepository.updateCarrier(editing.copy(deleted = true))
            _state.update {
                it.copy(showAddEditDialog = false, editingCarrier = null, error = result.error)
            }
        }
    }

    private fun restoreCarrier() {
        viewModelScope.launch {
            val editing = _state.value.editingCarrier ?: return@launch
            val result = carrierRepository.updateCarrier(editing.copy(deleted = false))
            _state.update {
                it.copy(showAddEditDialog = false, editingCarrier = null, error = result.error)
            }
        }
    }

    private fun reorderCarriers(orderedIds: List<String>) {
        // Apply the new order optimistically so the list doesn't snap back while the
        // batch write is in flight; the Firestore listener re-syncs the truth either way.
        _state.update { state ->
            val byId = state.carriers.associateBy { it.id }
            val reordered = orderedIds.mapIndexedNotNull { index, id -> byId[id]?.copy(order = index) }
            val untouched = state.carriers.filter { it.id !in orderedIds.toSet() }
            state.copy(carriers = reordered + untouched)
        }
        viewModelScope.launch {
            val result = carrierRepository.updateCarrierOrder(orderedIds)
            if (result.error != null) {
                _state.update { it.copy(error = result.error) }
            }
        }
    }
}
